import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Loader2 } from 'lucide-react';

export interface NewWorkspaceDialogProps {
  open: boolean;
  onClose: () => void;
  onCreateLocal: (name: string) => void;
  onJoinCloud: (inviteLink: string) => void;
  loading?: boolean;
  error?: string | null;
}

type Tab = 'create' | 'join';

export function NewWorkspaceDialog({
  open,
  onClose,
  onCreateLocal,
  onJoinCloud,
  loading = false,
  error = null,
}: NewWorkspaceDialogProps) {
  const [tab, setTab] = useState<Tab>('create');
  const [name, setName] = useState('');
  const [inviteLink, setInviteLink] = useState('');

  useEffect(() => {
    if (open) {
      setTab('create');
      setName('');
      setInviteLink('');
    }
  }, [open]);

  const handleCreate = () => {
    const trimmed = name.trim();
    if (trimmed && !loading) {
      onCreateLocal(trimmed);
    }
  };

  const handleJoin = () => {
    const trimmed = inviteLink.trim();
    if (trimmed && !loading) {
      onJoinCloud(trimmed);
    }
  };

  const handleOpenChange = (isOpen: boolean) => {
    if (!isOpen && !loading) {
      onClose();
    }
  };

  const tabBtnClass = (active: boolean) =>
    `flex-1 px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
      active ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground'
    }`;

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>New Workspace</DialogTitle>
          <DialogDescription className="sr-only">
            Create a new local workspace or join a cloud workspace
          </DialogDescription>
        </DialogHeader>

        <div className="flex gap-1 p-1 rounded-lg bg-muted">
          <button className={tabBtnClass(tab === 'create')} onClick={() => setTab('create')}>
            Create Local
          </button>
          <button className={tabBtnClass(tab === 'join')} onClick={() => setTab('join')}>
            Join Cloud
          </button>
        </div>

        {error && <p className="text-sm text-destructive">{error}</p>}

        {tab === 'create' && (
          <div className="flex flex-col gap-3">
            <Input
              placeholder="Workspace name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleCreate();
              }}
              disabled={loading}
              autoFocus
            />
            <div className="flex justify-end">
              <Button onClick={handleCreate} disabled={!name.trim() || loading}>
                {loading && <Loader2 className="size-4 animate-spin" />}
                Create
              </Button>
            </div>
          </div>
        )}

        {tab === 'join' && (
          <div className="flex flex-col gap-3">
            <Input
              placeholder="Paste invite link"
              value={inviteLink}
              onChange={(e) => setInviteLink(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleJoin();
              }}
              disabled={loading}
              autoFocus
            />
            <p className="text-xs text-muted-foreground">
              Paste a full URL or invite token from a collaborator
            </p>
            <div className="flex justify-end">
              <Button onClick={handleJoin} disabled={!inviteLink.trim() || loading}>
                {loading && <Loader2 className="size-4 animate-spin" />}
                Join
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
